import {
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
  useEventCallback,
} from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { useCallback, useState } from 'react';
import * as yup from 'yup';
import PmiTextField from 'shell/components/shared/text-field';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { DatePicker } from '@mui/lab';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { DateTime } from 'luxon';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import OperatorList, { idNoneSelected } from 'front-office/components/shared/operator-list';
import { ItemVM } from 'shell/view-models/item';
import { Operator } from 'shell/models/operator';

export interface CertificatesAnprForm {
  code?: string;
  certificateUpload?: { file: File | null; raw: string } | null;
  alias?: string;
  password?: string;
  startValidityDate?: DateTime | null;
  endValidityDate?: DateTime | null;
  lastOperator?: string;
  lastModifyDate?: DateTime | null;
  operatorName: string;
  operatorCode: string;
  operator?: (ItemVM & { operator?: Operator }) | null;
  contextLastUpdate?: string;
}

const validationSchema = yup.object({
  code: yup.string().required('Il campo codice è obbligatorio'),
  certificateUpload: yup.object().nullable().required('Il campo è obbligatorio'),
  alias: yup.string(),
  startValidityDate: yup.date().nullable().required('Campo obbligatorio'),
  endValidityDate: yup
    .date()
    .nullable()
    .typeError('Data non valida')
    .required('Campo obbligatorio')
    .when('startValidityDate', (dateFrom, formvalidationSchema) =>
      dateFrom
        ? formvalidationSchema.min(
            dateFrom,
            'La data deve essere maggiore o uguale della data di inizio'
          )
        : formvalidationSchema.nullable()
    ),
});

interface FormCertificatesAnprProps {
  onOk: (form?: CertificatesAnprForm) => void;
  onCancel: (form?: CertificatesAnprForm) => void;
  title: string;
  disabled?: boolean;
  initialValues?: CertificatesAnprForm;
  isEdit?: boolean;
  isNew?: boolean;
}

const FormCertificatesAnpr = ({
  onOk,
  onCancel,
  title,
  disabled = false,
  initialValues,
  isEdit,
  isNew,
}: FormCertificatesAnprProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const certificateForm = useFormik<CertificatesAnprForm>({
    initialValues: initialValues || {
      code: '',
      certificateUpload: null,
      alias: '',
      password: '',
      startValidityDate: null,
      endValidityDate: null,
      lastOperator: '',
      lastModifyDate: null,
      contextLastUpdate: '',
      operatorName: '',
      operatorCode: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);

  const handleVisibilityPassword = useEventCallback(() => {
    setPasswordVisible((oldPasswordVisibile) => !oldPasswordVisibile);
  });

  const handleAddFile = useEventCallback((file: File) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};
      // Do something with result
      certificateForm.setFieldValue('certificateUpload', {
        raw: result as string,
        file,
      });
    });

    reader.readAsDataURL(file);
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={certificateForm.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="ID Certificato"
                  label="ID Certificato *"
                  id="code"
                  name="code"
                  fullWidth
                  type="text"
                  variant="outlined"
                  inputProps={{
                    maxLength: '14',
                  }}
                  autoComplete="off"
                  value={certificateForm.values.code}
                  disabled={disabled || isEdit}
                  onChange={certificateForm.handleChange}
                  error={certificateForm.touched.code && Boolean(certificateForm.errors.code)}
                  helperText={certificateForm.errors.code}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <PmiTooltipError
                  show={
                    certificateForm.touched.certificateUpload &&
                    Boolean(certificateForm.errors.certificateUpload)
                  }
                  message={certificateForm.errors.certificateUpload}
                >
                  <span>
                    Carica file certificato&nbsp;
                    <PmiInputFile
                      multiple={false}
                      disabled={disabled}
                      file={certificateForm.values.certificateUpload?.file || undefined}
                      onChange={handleAddFile}
                      color="cdmBrandThird"
                    />
                  </span>
                </PmiTooltipError>
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  label="Alias"
                  id="alias"
                  name="alias"
                  fullWidth
                  type="text"
                  inputProps={{
                    maxLength: '14',
                  }}
                  variant="outlined"
                  value={certificateForm.values.alias || certificateForm.values.code || ''}
                  disabled={disabled}
                  onChange={certificateForm.handleChange}
                  error={certificateForm.touched.alias && Boolean(certificateForm.errors.alias)}
                  helperText={certificateForm.errors.alias}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  label="Password"
                  aria-label="password"
                  id="password"
                  name="password"
                  fullWidth
                  type={passwordVisible ? 'text' : 'password'}
                  variant="outlined"
                  inputProps={{
                    maxLength: '24',
                  }}
                  autoComplete="off"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="cambia visibilità password"
                          onClick={handleVisibilityPassword}
                        >
                          {!passwordVisible ? (
                            <Visibility color="secondary" />
                          ) : (
                            <VisibilityOff color="secondary" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  value={certificateForm.values?.password || ''}
                  disabled={disabled}
                  onChange={(event) =>
                    certificateForm.setFieldValue('password', event.target.value)
                  }
                  error={
                    certificateForm.touched?.password && Boolean(certificateForm.errors?.password)
                  }
                  helperText={certificateForm.errors?.password}
                />
              </Grid>
              <Grid item container spacing={2} xs={12}>
                {isNew ? (
                  <Grid item xs={12} md={3}>
                    <OperatorList
                      onChange={(event, operator) => {
                        if (operator?.id === idNoneSelected) {
                          return;
                        }
                        certificateForm.setValues({
                          ...certificateForm.values,
                          operatorCode: operator?.id?.toString() || '',
                          operatorName: operator?.value || '',
                          operator: operator as (ItemVM & { operator?: Operator }) | null,
                        });
                      }}
                      idService="TUTTI"
                      operatorType="0"
                      isAnpr
                      label="Codice Operatore"
                      value={certificateForm?.values?.operator?.id?.toString() || ''}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} md={3}>
                    <PmiTextField
                      label="CD Operatore *"
                      id="operatorCode"
                      name="operatorCode"
                      fullWidth
                      type="text"
                      variant="outlined"
                      value={certificateForm.values.operatorCode || ''}
                      disabled
                      onChange={certificateForm.handleChange}
                      error={
                        certificateForm.touched.operatorCode &&
                        Boolean(certificateForm.errors.operatorCode)
                      }
                      helperText={certificateForm.errors.operatorCode}
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={9}>
                  <PmiTextField
                    label="Cognome e nome *"
                    id="operatorName"
                    name="operatorName"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={certificateForm.values.operatorName || ''}
                    disabled
                    onChange={certificateForm.handleChange}
                    error={
                      certificateForm.touched.operatorName &&
                      Boolean(certificateForm.errors.operatorName)
                    }
                    helperText={certificateForm.errors.operatorName}
                  />
                </Grid>
              </Grid>
              <Grid item container spacing={2} xs={12}>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Data inizio validità"
                    aria-label="Data inizio validità"
                    value={certificateForm.values.startValidityDate || null}
                    disabled={disabled}
                    onChange={(date) => certificateForm.setFieldValue('startValidityDate', date)}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        id="startValidityDate"
                        error={
                          certificateForm.touched.startValidityDate &&
                          Boolean(certificateForm.errors.startValidityDate)
                        }
                        helperText={certificateForm.errors.startValidityDate}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Data fine validità"
                    aria-label="Data fine validità"
                    value={certificateForm.values.endValidityDate || null}
                    disabled={disabled}
                    onChange={(date) => certificateForm.setFieldValue('endValidityDate', date)}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        id="endValidityDate"
                        error={
                          certificateForm.touched.endValidityDate &&
                          Boolean(certificateForm.errors.endValidityDate)
                        }
                        helperText={certificateForm.errors.endValidityDate}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  label="Operatore ultima modifica"
                  id="lastOperator"
                  name="lastOperator"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={certificateForm.values.lastOperator || ''}
                  disabled
                  onChange={certificateForm.handleChange}
                  error={
                    certificateForm.touched.lastOperator &&
                    Boolean(certificateForm.errors.lastOperator)
                  }
                  helperText={certificateForm.errors.lastOperator}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Data ultima modifica"
                  aria-label="Data ultima modifica"
                  disabled
                  value={certificateForm.values.lastModifyDate || null}
                  onChange={(date) => certificateForm.setFieldValue('lastModifyDate', date)}
                  renderInput={(params) => (
                    <PmiTextField
                      {...params}
                      id="lastModifyDate"
                      error={
                        certificateForm.touched.lastModifyDate &&
                        Boolean(certificateForm.errors.lastModifyDate)
                      }
                      helperText={certificateForm.errors.lastModifyDate}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  label="Nota Operatore"
                  id="contextLastUpdate"
                  name="contextLastUpdate"
                  fullWidth
                  inputProps={{
                    maxLength: '50',
                  }}
                  variant="outlined"
                  value={certificateForm.values.contextLastUpdate}
                  onChange={certificateForm.handleChange}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(certificateForm.values);
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormCertificatesAnpr.displayName = 'Form Certificates Anpr component';

export default FormCertificatesAnpr;
